import { useState } from 'react'
import { isAxiosError } from 'axios'
import { Button, Group, Loader, Stack, Text } from '@mantine/core'
import { IconCloudDownload } from '@tabler/icons-react'
import { useQueryClient } from '@tanstack/react-query'

import {
  CHAVE_OTA, cancelarOta, OTA_ATIVOS, pedirOta, useOtaDoRobo,
  type ModeloDeRelease, type MotivoOta, type OrdemOta, type RoboFrota, type StatusOta,
} from '@/api/robo'
import { candidatas } from './candidatas'
import { Callout } from '@/components/ui'

const STATUS: Record<StatusOta, string> = {
  pendente: 'Esperando o robô buscar',
  baixando: 'Baixando e gravando',
  aplicado: 'Aplicada',
  falhou: 'Não aplicada',
  cancelado: 'Cancelada',
}

/** Vocabulario fechado do servidor — cada motivo diz o que fazer a seguir. */
export const MOTIVO: Record<MotivoOta, string> = {
  perfil: 'O robô recusou: a placa é de outro modelo. Confira o modelo cadastrado.',
  tamanho: 'O robô recusou: o firmware não cabe na partição dele. Grave pela USB.',
  sha256: 'O arquivo chegou diferente do publicado. O robô descartou e segue na versão anterior.',
  rede: 'A conexão caiu durante o download. O robô segue na versão anterior.',
  escrita: 'A gravação na memória do robô falhou. O robô segue na versão anterior.',
  voltou: 'A versão nova não se confirmou e o robô voltou sozinho para a anterior.',
  expirou: 'O robô não buscou a atualização em 24 horas — desligado ou fora da rede?',
}

const SEGUE = 'O robô segue na versão anterior.'

/** A frase da falha: com o `detalhe` de `rede`, a porta que fechou; sem ele
 * (firmware antigo, ou outro motivo), a frase do motivo. */
export function fraseDaFalha(ordem: OrdemOta): string {
  const d = ordem.detalhe
  if (ordem.motivo === 'rede' && d) {
    const baixado = `${Math.round(d.bytes / 1024).toLocaleString('pt-BR')} KB baixados em ${Math.round(d.ms / 1000)} s`
    switch (d.porta) {
      case 'prazo': return `O download passou do prazo total, com ${baixado}. ${SEGUE}`
      case 'parado': return `O download parou: 30 s sem receber nada, com ${baixado}. ${SEGUE}`
      case 'fechou': return `A conexão caiu no meio do download, com ${baixado}. ${SEGUE}`
      case 'http': return `O console respondeu ${d.http} ao pedido do arquivo. ${SEGUE}`
      case 'conexao':
        return `O robô não conseguiu abrir a conexão com o console${d.http ? ` (código ${d.http})` : ''}. ${SEGUE}`
    }
  }
  return ordem.motivo ? MOTIVO[ordem.motivo] : ''
}

/** O `detail` dos 409 do pedido, em frase de quem administra. */
const RECUSA: Record<string, string> = {
  robo_nao_ativo: 'Só robô ativo recebe atualização.',
  modelo_nao_cadastrado: 'Cadastre o modelo de hardware do robô antes.',
  modelo_diverge: 'O robô informa ser de outro modelo. Confira o cadastro antes de atualizar.',
  ja_atualizado: 'O robô já roda a versão publicada.',
  ordem_ativa: 'Já há uma atualização em andamento.',
  firmware_nao_publicado: 'Ainda não há firmware publicado para o modelo deste robô.',
  sem_ordem_ativa: 'A atualização já tinha terminado.',
  transicao_recusada: 'A atualização já tinha terminado.',
}

function frase(erro: unknown): string {
  const detail = isAxiosError(erro) ? erro.response?.data?.detail : undefined
  return (typeof detail === 'string' && RECUSA[detail]) || 'Não foi possível falar com o console.'
}

/**
 * Pede que o robô baixe e grave sozinho a versão publicada. O robô confere o
 * modelo, o tamanho e o `sha256` antes de trocar, e volta sozinho para a
 * versão anterior se a nova não se confirmar.
 */
export function AtualizarPeloAr({ robo, modelos }: { robo: RoboFrota; modelos?: ModeloDeRelease[] }) {
  const qc = useQueryClient()
  const ota = useOtaDoRobo(robo.public_id)
  const [enviando, setEnviando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  if (ota.isLoading) return <Loader size="sm" />
  if (ota.isError) {
    return <Text size="sm" c="red.7">Não foi possível consultar a atualização pelo ar.</Text>
  }
  const ordem = ota.data?.ordem ?? null
  // A candidata que o botão entregaria: o topo do changelog do modelo, quando
  // ainda não homologado (F3). O robô que já a roda não vê a frase.
  const modelo = modelos?.find((m) => m.perfil === robo.firmware?.perfil)
  const topo = modelo ? candidatas(modelo)[0] : undefined
  const candidataNoBotao = topo && robo.firmware?.versao !== topo.versao ? topo.versao : null
  const ordemCandidata = ordem != null && ota.data?.candidata === true
  const ativa = ordem != null && OTA_ATIVOS.includes(ordem.status)

  const agir = async (acao: () => Promise<unknown>) => {
    setEnviando(true)
    setErro(null)
    try {
      await acao()
    } catch (e) {
      setErro(frase(e))
    } finally {
      setEnviando(false)
      await qc.invalidateQueries({ queryKey: CHAVE_OTA(robo.public_id) })
    }
  }

  return (
    <Stack gap="sm">
      {ordem && (
        <Stack gap={2}>
          <Text size="sm">
            {STATUS[ordem.status]} · <Text span ff="monospace" size="sm">{ordem.versao}</Text>
          </Text>
          {ordem.status === 'falhou' && ordem.motivo && (
            <Text size="sm" c="dimmed">{fraseDaFalha(ordem)}</Text>
          )}
          {ordemCandidata && (
            <Text size="xs" c="dimmed">Versão ainda não homologada — use num robô de bancada.</Text>
          )}
        </Stack>
      )}
      {erro && <Callout variant="warning">{erro}</Callout>}
      <Group gap="sm">
        {ativa ? (
          <Button variant="default" loading={enviando}
            onClick={() => agir(() => cancelarOta(robo.public_id))}>
            Cancelar atualização
          </Button>
        ) : (
          <Button leftSection={<IconCloudDownload size={16} />} loading={enviando}
            onClick={() => agir(() => pedirOta(robo.public_id))}>
            {candidataNoBotao ? `Atualizar para a candidata ${candidataNoBotao}` : 'Atualizar pelo ar'}
          </Button>
        )}
      </Group>
      {!ativa && candidataNoBotao && (
        <Text size="xs" c="dimmed">Ainda não homologada — use num robô de bancada.</Text>
      )}
    </Stack>
  )
}
